import './ProjectsListContainer.scss';
import { useInjection } from 'inversify-react';
import { action } from 'mobx';
import { observer } from 'mobx-react-lite';
import { ReactElement, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useOnMount } from '../../../../common/hooks/useOnMount';
import { S_TabsCard } from '../../../../design/card/tabsCard/S_TabsCards';
import { IOverlayLoaderStore } from '../../../../modules/common/OverlayLoader.store';
import { ICurrentWorkspaceStore } from '../../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { CurrentWorkspaceChangedEventType } from '../../../../modules/workspaces/currentWorkspace/events/CurrentWorkspaceChangedEvent';
import { useLoader } from '../../../helpers/useLoader';
import { ACDLError } from '../../../models/error.model';
import { Home } from '../../../routes/config/Home';
import { IEventBus } from '../../../services/eventBus.service';
import { useClarificationsPermissions } from '../../annotation/submodules/clarifications/Clarifications.permissions';
import { IImagesUploaderService } from '../../datasets/imagesUpload.service';
import { IProjectDetailsBl } from '../../projectDetails/projectDetails.bl';
import { IImageFilters, ImageDefaultFilters } from '../../projectDetails/sub/images/imageFilters.model';
import { ImageAnnotationStatus } from '../../projectDetails/sub/images/projectDetailsImages.model';
import { IImageFilterService } from '../../projectDetails/sub/images/services/imageFilters.service';
import { IUserService } from '../../user/user.service';
import { ProjectsList } from '../components/ProjectsList';
import { useProjectsService } from '../projects.service';
import { ProjectOwnership } from '../projects.store';
import { ProjectsListControlsContainer } from './ProjectsListControls.container';
import { ProjectsTabs } from './ProjectsTabs';

export const ProjectsListContainer = observer((): ReactElement => {
  const projectsService = useProjectsService();
  const projectDetailsService = useInjection(IProjectDetailsBl.$);
  const imagesUploaderService = useInjection(IImagesUploaderService.$);
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);
  const overlayLoader = useInjection(IOverlayLoaderStore.$);
  const userService = useInjection(IUserService.$);
  const imageFilterService = useInjection(IImageFilterService.$);
  const clarificationsPermissions = useClarificationsPermissions();
  const eventBus = useInjection(IEventBus.$);
  const { isLoading, withLoaderAsync } = useLoader();
  const location = useLocation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalProjectId, setModalProjectId] = useState<string | undefined>(undefined);
  const [projectDatasetsToDelete, setProjectDatasetsToDelete] = useState<string[]>([]);
  const history = useHistory();

  useEffect(() => {
    const currentWorkspaceChangeListenerDisposer = eventBus.addListener(refreshAsync, CurrentWorkspaceChangedEventType);
    return (): void => {
      currentWorkspaceChangeListenerDisposer();
    };
  }, []);

  useEffect(() => {
    let ownership: ProjectOwnership = ProjectOwnership.All;
    const workspaceId = currentWorkspaceStore.currentWorkspace.id;

    switch (location.pathname) {
      case Home.Projects.List.All.withParams({ workspaceId }):
        ownership = ProjectOwnership.All;
        break;
      case Home.Projects.List.Owned.withParams({ workspaceId }):
        ownership = ProjectOwnership.My;
        break;
      case Home.Projects.List.Others.withParams({ workspaceId }):
        ownership = ProjectOwnership.Others;
        break;
    }

    void projectsService.changeOwnershipFilter(ownership);
  }, [location.pathname, currentWorkspaceStore.currentWorkspace.id]);

  const refreshAsync = async (): Promise<void> => {
    await Promise.all([projectsService.refreshAsync(), userService.getUserInfoAsync()]);
  };

  const deleteProject = async (projectId: string): Promise<void> => {
    setShowDeleteModal(true);
    setModalProjectId(projectId);

    const result = await projectsService.getProjectExclusiveDatasetsAsync(projectId);
    if (!(result instanceof ACDLError)) {
      setProjectDatasetsToDelete(result);
    }
  };

  const handleDeleteCancel = (): void => {
    setShowDeleteModal(false);
    setModalProjectId(undefined);
  };

  const handleDeleteConfirm = async (deleteDatasets: boolean): Promise<void> => {
    if (isLoading || !modalProjectId) {
      return;
    }

    await withLoaderAsync(async () => {
      setProjectDatasetsToDelete([]);

      if (deleteDatasets) {
        const result = await projectDetailsService.getProjectDetailsDatasetsAsync(modalProjectId);
        if (!(result instanceof ACDLError) && result.projectDatasets.length > 0) {
          const promises = result.projectDatasets.flatMap((datasetToRemove) => [imagesUploaderService.cancelUpload(datasetToRemove.id)]);
          await Promise.allSettled(promises);
        }
      }

      await projectsService.deleteProjectAsync(modalProjectId, deleteDatasets);
      await projectsService.refreshAsync();
      setShowDeleteModal(false);
    }, 'delete-project-button');
  };

  const handleOrderChange = async (orderBy: string, orderType: string): Promise<void> => {
    await projectsService.changeOrder(orderBy, orderType);
  };

  const handlePaginationChange = async (pageNumber: number, pageSize: number): Promise<void> => {
    await projectsService.changePagination(pageNumber, pageSize);
  };

  const handleAnnotate = action((projectId: string) => {
    history.push(Home.Annotations.Create.withParams({ projectId, workspaceId: currentWorkspaceStore.currentWorkspace.id }));
  });

  const handleReview = action((projectId: string) => {
    history.push(Home.Annotations.Review.withParams({ projectId, workspaceId: currentWorkspaceStore.currentWorkspace.id }));
  });

  const handleGoToClarifications = async (projectId: string): Promise<void> => {
    const clarificationFilter: IImageFilters = { ...ImageDefaultFilters, annotationStatuses: [ImageAnnotationStatus.CLARIFICATION] };
    const filterId = await imageFilterService.upsertFiltersAsync(projectId, clarificationFilter);
    const imageId = projectsService.store.projectsList.find((p) => p.id === projectId)?.unansweredClarificationImageId;

    if (imageId) {
      history.push(
        Home.FreeAccess.View.withParams({
          projectId,
          imageId,
          filterId,
          workspaceId: currentWorkspaceStore.currentWorkspace.id,
        })
      );
    }
  };

  const canClarify = clarificationsPermissions.canClarify();

  useOnMount(async () => {
    await refreshAsync();
  });

  return (
    <div className='projects-list-container'>
      <div className='projects-list-controls'>
        <ProjectsTabs workspaceId={currentWorkspaceStore.currentWorkspace.id} />
        <ProjectsListControlsContainer />
      </div>
      <S_TabsCard>
        <ProjectsList
          isLoading={overlayLoader.isSpinnerVisible('project-list')}
          workspaceId={currentWorkspaceStore.currentWorkspace.id}
          projects={projectsService.store.projectsList}
          projectsListViewMode={userService.data.projectListViewMode}
          showDeleteModal={showDeleteModal}
          pagination={{
            pageNumber: projectsService.store.projectsPaging.pageNumber,
            pageSize: projectsService.store.projectsPaging.pageSize,
            totalCount: projectsService.store.projectsPaging.totalCount,
            onChange: handlePaginationChange,
          }}
          sorting={{
            orderBy: projectsService.store.projectsPaging.orderBy,
            orderType: projectsService.store.projectsPaging.orderType,
          }}
          onAnnotate={handleAnnotate}
          onReview={handleReview}
          onDelete={deleteProject}
          onDeleteConfirmed={handleDeleteConfirm}
          onDeleteCanceled={handleDeleteCancel}
          onOrderingChange={handleOrderChange}
          projectDatasetsToDelete={projectDatasetsToDelete}
          onGoToClarifications={canClarify.allowed ? handleGoToClarifications : undefined}
        />
      </S_TabsCard>
    </div>
  );
});
